
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Users, Shield, Calendar, Camera, Settings } from "lucide-react";

const Help = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Zurück zur Startseite
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Hilfe & Anleitungen</h1>
            <p className="text-xl text-gray-600">Alles was Sie wissen müssen, um uplivion optimal zu nutzen.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-6">
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Calendar className="w-8 h-8 text-uplivion-blue" />
                  <h2 className="text-xl font-semibold">Timeline erstellen</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Lernen Sie, wie Sie Ihre erste Timeline erstellen und Ihre Lebensgeschichte strukturieren.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Erste Schritte:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>1. Nach der Registrierung wird automatisch eine Timeline für Sie erstellt</li>
                    <li>2. Die Timeline ist in Lebensphasen unterteilt (Kindheit, Jugend, etc.)</li>
                    <li>3. Klicken Sie auf eine Phase, um sie zu erweitern</li>
                    <li>4. Fügen Sie Ihre ersten Erinnerungen hinzu</li>
                  </ul>
                </div>
              </div>

              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Camera className="w-8 h-8 text-uplivion-orange" />
                  <h2 className="text-xl font-semibold">Erinnerungen hinzufügen</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  So fügen Sie Fotos, Texte und Details zu Ihren wichtigsten Lebensmomenten hinzu.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Erinnerung erstellen:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Klicken Sie auf "+" in einer Lebensphase</li>
                    <li>• Geben Sie einen Titel und Beschreibung ein</li>
                    <li>• Wählen Sie ein Datum (optional genauer Monat/Tag)</li>
                    <li>• Laden Sie Fotos hoch (mehrere möglich)</li>
                    <li>• Stellen Sie die Privatsphäre-Einstellungen ein</li>
                  </ul>
                </div>
              </div>

              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                  <h2 className="text-xl font-semibold">Freunde & Teilen</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Verbinden Sie sich mit Freunden und teilen Sie ausgewählte Erinnerungen.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Freunde hinzufügen:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Gehen Sie zum Freunde-Bereich</li>
                    <li>• Suchen Sie nach Benutzernamen oder E-Mail</li>
                    <li>• Senden Sie eine Freundschaftsanfrage</li>
                    <li>• Akzeptieren Sie eingehende Anfragen</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-8 h-8 text-purple-600" />
                  <h2 className="text-xl font-semibold">Privatsphäre-Einstellungen</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Kontrollieren Sie, wer welche Ihrer Erinnerungen sehen kann.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Sichtbarkeits-Optionen:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Nur ich:</strong> Privat, nur Sie können es sehen</li>
                    <li>• <strong>Freunde:</strong> Alle Ihre Freunde können es sehen</li>
                    <li>• <strong>Öffentlich:</strong> Jeder kann es sehen (in Planung)</li>
                  </ul>
                </div>
              </div>

              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Settings className="w-8 h-8 text-gray-600" />
                  <h2 className="text-xl font-semibold">Konto-Verwaltung</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Verwalten Sie Ihr Profil, Einstellungen und Benachrichtigungen.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Profil bearbeiten:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Gehen Sie zu Ihrem Profil</li>
                    <li>• Klicken Sie auf "Profil bearbeiten"</li>
                    <li>• Aktualisieren Sie Ihre Informationen</li>
                    <li>• Ändern Sie Ihr Profilbild</li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <BookOpen className="w-8 h-8 text-blue-600" />
                  <h2 className="text-xl font-semibold text-blue-900">Beta-Features</h2>
                </div>
                <p className="text-blue-800 mb-4">
                  Als Beta-Nutzer haben Sie Zugang zu neuen Features, bevor sie für alle verfügbar sind.
                </p>
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-blue-900">Kommende Features:</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Timeline-Vorlagen und Themes</li>
                    <li>• Erweiterte Such- und Filterfunktionen</li>
                    <li>• Export-Funktionen (PDF, Buch)</li>
                    <li>• Familien-Timelines</li>
                    <li>• Mobile App für iOS/Android</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">Häufige Probleme & Lösungen</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2">Fotos werden nicht hochgeladen</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Überprüfen Sie Ihre Internetverbindung</li>
                  <li>• Maximal 10MB pro Foto</li>
                  <li>• Unterstützte Formate: JPG, PNG, WEBP</li>
                  <li>• Versuchen Sie es mit einem anderen Browser</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Timeline lädt nicht</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Aktualisieren Sie die Seite (F5)</li>
                  <li>• Leeren Sie den Browser-Cache</li>
                  <li>• Deaktivieren Sie Adblocker temporär</li>
                  <li>• Verwenden Sie einen modernen Browser</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Freunde werden nicht gefunden</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Suchen Sie nach dem exakten Benutzernamen</li>
                  <li>• Überprüfen Sie die Schreibweise</li>
                  <li>• Probieren Sie die E-Mail-Adresse</li>
                  <li>• Bitten Sie Freunde, Sie zu suchen</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Benachrichtigungen kommen nicht an</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Überprüfen Sie Ihren Spam-Ordner</li>
                  <li>• Kontrollieren Sie die E-Mail-Einstellungen</li>
                  <li>• Fügen Sie uplivion.life zu Ihren Kontakten hinzu</li>
                  <li>• Überprüfen Sie die Benachrichtigungs-Einstellungen</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold mb-4">Weitere Hilfe benötigt?</h2>
            <p className="text-gray-600 mb-6">
              Konnten wir Ihre Frage nicht beantworten? Unser Support-Team hilft gerne weiter.
            </p>
            <div className="flex justify-center gap-4">
              <Button asChild>
                <Link to="/contact">Kontakt aufnehmen</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/faq">FAQ durchsuchen</Link>
              </Button>
            </div>
          </div>

          <div className="pt-8 border-t">
            <Button asChild>
              <Link to="/">Zur Startseite</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
